import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { ArrowRight, Heart, Award, Leaf, Users } from 'lucide-react';

const BOOKING_URL = 'https://book.abbamassage.com/?aff=29';

const IMAGES = {
  team: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/15z1ydle_TERAPEUTAS%20ABBA.JPG',
  studio: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/p81db8rj_SALA%20DE%20ESTAR%20ABBA%20ESTUDIO.JPG',
  inHome: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/uz18d6um_in%20home.jpg',
};

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": "https://abbamassage.com/about#page",
      "url": "https://abbamassage.com/about",
      "name": "About ABBA Massage",
      "description": "Meet the certified therapist team behind ABBA Massage Studio & Wellness Center in Cancún and learn about our approach to personalized wellness.",
      "mainEntity": {
        "@id": "https://abbamassage.com/#business"
      }
    },
    {
      "@type": [
        "DaySpa",
        "HealthAndBeautyBusiness"
      ],
      "@id": "https://abbamassage.com/#business",
      "name": "ABBA Massage Studio & Wellness Center",
      "url": "https://abbamassage.com/",
      "description": "Private massage studio and in-home wellness service in Cancún led by professionals with extensive luxury spa and hospitality experience.",
      "image": "https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/15z1ydle_TERAPEUTAS%20ABBA.JPG",
      "telephone": "+52 998 303 2313",
      "email": "hello@abbamassage.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Calle Playa Brava 26, Manzana 1, Lote 53",
        "addressLocality": "Cancún",
        "addressRegion": "Quintana Roo",
        "postalCode": "77508",
        "addressCountry": "MX"
      },
      "knowsLanguage": [
        "English",
        "Spanish"
      ],
      "sameAs": [
        "https://www.instagram.com/abbamassage",
        "https://www.facebook.com/abbamassage",
        "https://maps.app.goo.gl/G2JL5nKRZdTESBVXA",
        "https://www.tripadvisor.com.mx/Attraction_Review-g150807-d34151326-Reviews-Abba_Massage_Studio_and_Wellness_Center-Cancun_Yucatan_Peninsula.html"
      ]
    }
  ]
};
const FOUNDER_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": "https://abbamassage.com/about#founder",
  "name": "Mariana Castañeda",
  "jobTitle": "Founder & CEO of ABBA Massage",
  "worksFor": {
    "@id": "https://abbamassage.com/#business"
  },
  "knowsAbout": [
    "Massage Therapy",
    "Spa Management",
    "Holistic Wellness",
    "Luxury Spa Operations",
    "Wellness Hospitality"
  ]
};
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

const valueIcons = [Award, Heart, Users, Leaf];

export const About = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    const isSpanish = language === 'es';
    const title = isSpanish
      ? 'Conoce ABBA Massage | Nuestro Equipo en Cancún'
      : 'About ABBA Massage | Our Team & Story in Cancun';
    const description = isSpanish
      ? 'Conoce la historia y al equipo certificado de ABBA Massage en Cancún, con amplia experiencia en spas de lujo, masajes y bienestar personalizado.'
      : 'Meet the certified team behind ABBA Massage in Cancun, with extensive luxury spa experience and a personalized approach to massage and wellness.';
    const canonicalUrl = 'https://abbamassage.com/about';

    document.title = title;

    const setMeta = (attribute, key, value) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', IMAGES.team);
    setMeta('property', 'og:locale', isSpanish ? 'es_MX' : 'en_US');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', IMAGES.team);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    const staticSchema = document.getElementById('about-static-schema');
    let schemaScript = null;
    if (!staticSchema) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'about-schema';
      schemaScript.type = 'application/ld+json';
      schemaScript.text = JSON.stringify(ABOUT_SCHEMA);
      document.head.appendChild(schemaScript);
    }

    return () => {
      schemaScript?.remove();
    };
  }, [language]);

  return (
    <div data-testid="about-page"> <script
  type="application/ld+json"
  dangerouslySetInnerHTML={{ __html: JSON.stringify(FOUNDER_SCHEMA) }}
/>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 min-h-[50vh] md:min-h-[60vh] flex items-center" style={{ backgroundColor: '#5C1A33' }}>
        <div className="absolute inset-0 z-0 flex items-center justify-center">
          <img
            src={IMAGES.team}
            alt="ABBA Massage Team"
            className="max-w-full max-h-full object-contain"
            style={{ width: 'auto', height: '100%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {t('about.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {t('about.title')}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 px-6" data-testid="about-story">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6 tracking-tight">
                {t('about.story.title')}
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] leading-relaxed font-['Manrope']">
                {t('about.story.content')}
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="absolute -top-4 -left-4 w-full h-full bg-[#C9B99A]/20 rounded-2xl" />
              <img
                src={IMAGES.studio}
                alt="ABBA Studio"
                className="relative rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#F8F6F3] px-6" data-testid="about-values">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] tracking-tight">
              {t('about.values.title')}
            </motion.h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
          >
            {t('about.values.items').map((value, index) => {
              const Icon = valueIcons[index];
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl p-8 text-center shadow-xl"
                >
                  <div className="w-16 h-16 rounded-full bg-[#F8F6F3] mx-auto mb-6 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-[#A91B60]" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C]">
                    {value}
                  </h3>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 md:py-32 px-6" data-testid="about-team">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-4 tracking-tight">
              {t('about.team.title')}
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] font-['Manrope']">
              {t('about.team.description')}
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex justify-center"
          >
            <img
              src={IMAGES.team}
              alt="ABBA Massage Team"
              className="w-full max-w-4xl rounded-2xl shadow-xl object-contain"
              style={{ height: 'auto' }}
            />
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#A91B60]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-6 tracking-tight">
              Ready to experience <em className="italic text-[#C9B99A]">ABBA</em>?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-10 font-['Manrope']">
              Book your session and begin your journey to wellness.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B99A] hover:bg-[#C49366] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {t('cta.bookNow')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
